import React, { Suspense, lazy, useState } from 'react';
import { AppProvider, useApp } from './contexts/AppContext';
import AuthWrapper from './components/auth/AuthWrapper';
import Navigation from './components/navigation/Navigation';
import Dashboard from './components/screens/Dashboard';
import Tasks from './components/screens/Tasks';
import Calendar from './components/screens/Calendar';
import GanttChart from './components/screens/GanttChart';
import Profile from './components/screens/Profile';
import Connections from './components/screens/Connections';
import SenseiAI from './components/screens/SenseiAI';
import Habits from './components/screens/Habits';
import Lists from './components/screens/Lists';
import MoodTracker from './components/screens/MoodTracker';
import Affirmations from './components/screens/Affirmations';
import PersonalNotes from './components/screens/PersonalNotes';
import Budget from './components/screens/Budget';
import Analytics from './components/screens/Analytics';
import Admin from './components/screens/Admin';
import Connectors from './components/screens/Connectors';
import SupportChat from './components/screens/SupportChat';
import CoolFeatures from './components/screens/CoolFeatures';
import { ConnectionsProvider } from './contexts/ConnectionsContext';
import { GanttChartsProvider } from './contexts/GanttChartsContext';
import { ChatProvider } from './contexts/ChatContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';

const AppContent: React.FC = () => {
  const { state } = useApp();
  const { isDark } = useTheme();
  const [currentScreen, setCurrentScreen] = useState<'dashboard' | 'tasks' | 'gantt' | 'calendar' | 'connections' | 'sensei' | 'habits' | 'lists' | 'mood' | 'affirmations' | 'notes' | 'budget' | 'support' | 'analytics' | 'admin' | 'connectors' | 'settings'>('dashboard');

  // Public pages (no auth required)
  if (window.location.pathname === '/cool-features') {
    return <CoolFeatures />;
  }

  /**
   * Seeded design previews -- see src/preview/TasksPreview.tsx.
   *
   * Build-time gated, and deliberately not on NODE_ENV: these are checked
   * against a *production* build, because the dev server's Tailwind does not
   * emit the dojo utilities. So the deploy build simply omits the flag and CRA
   * inlines the constant, letting the whole branch and the preview module drop
   * out of the bundle. To look at them:
   *
   *     REACT_APP_PREVIEW=1 npx react-scripts build
   */
  if (process.env.REACT_APP_PREVIEW === '1') {
    const path = window.location.pathname;
    // Lazy, so the seeded fixtures live in their own chunk rather than the main
    // bundle. With the flag off the branch is dead and webpack drops the
    // import entirely -- no preview code, and no fake names, reach production.
    const TasksPreview = lazy(() => import('./preview/TasksPreview'));
    const show = (node: React.ReactNode) => <Suspense fallback={null}>{node}</Suspense>;
    if (path === '/__preview') return show(<TasksPreview />);
    if (path === '/__preview/dashboard') return show(<TasksPreview screen="dashboard" />);
    if (path === '/__preview/habits') return show(<TasksPreview screen="habits" />);
    if (path === '/__preview/calendar') return show(<TasksPreview screen="calendar" />);
    if (path === '/__preview/lists') return show(<TasksPreview screen="lists" />);
    if (path === '/__preview/gantt') return show(<TasksPreview screen="gantt" />);
    if (path === '/__preview/connections') return show(<TasksPreview screen="connections" />);
    if (path === '/__preview/settings') return show(<TasksPreview screen="settings" />);
    if (path === '/__preview/notes') return show(<TasksPreview screen="notes" />);
    if (path === '/__preview/sensei') return show(<TasksPreview screen="sensei" />);
    if (path === '/__preview/support') return show(<TasksPreview screen="support" />);
    if (path === '/__preview/affirmations') return show(<TasksPreview screen="affirmations" />);
    if (path === '/__preview/mood') return show(<TasksPreview screen="mood" />);
    if (path === '/__preview/budget') return show(<TasksPreview screen="budget" />);
    if (path === '/__preview/analytics') return show(<TasksPreview screen="analytics" />);
  }

  if (state.loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-dojo-ink' : 'bg-dojo-cream'}`}>
        <div className="text-center">
          <div className={`animate-spin rounded-full h-12 w-12 border-b-2 ${isDark ? 'border-white' : 'border-black'} mx-auto mb-4`}></div>
          <p className={`text-lg ${isDark ? 'text-neutral-100' : 'text-black'}`}>Loading...</p>
        </div>
      </div>
    );
  }

  if (!state.user) {
    return <AuthWrapper />;
  }

  // PAYWALL REMOVED (2026-08): the whole web app used to be behind this gate.
  // Signed-in users now go straight through to the app.

  const renderCurrentScreen = () => {
    switch (currentScreen) {
      case 'dashboard':
        return <Dashboard onNavigate={setCurrentScreen} />;
      case 'tasks':
        return <Tasks />;
      case 'gantt':
        return <GanttChart />;
      case 'calendar':
        return <Calendar />;
      case 'connections':
        return <Connections />;
      case 'sensei':
        return <SenseiAI />;
      case 'habits':
        return <Habits />;
      case 'lists':
        return <Lists />;
      case 'mood':
        return <MoodTracker />;
      case 'affirmations':
        return <Affirmations />;
      case 'notes':
        return <PersonalNotes />;
      case 'budget':
        return <Budget />;
      case 'support':
        return <SupportChat />;
      case 'analytics':
        return <Analytics />;
      case 'connectors':
        return <Connectors />;
      case 'admin':
        return <Admin />;
      case 'settings':
        return <Profile onNavigate={setCurrentScreen} />;
      default:
        return <Dashboard onNavigate={setCurrentScreen} />;
    }
  };

  return (
    <ConnectionsProvider user={state.user}>
      <GanttChartsProvider user={state.user}>
        <ChatProvider user={state.user}>
          <div className={`min-h-screen ${isDark ? 'bg-dojo-ink' : 'bg-dojo-cream'}`}>
            <Navigation currentScreen={currentScreen} onNavigate={setCurrentScreen} />

            {/* Main Content Area */}
            <div className="lg:pl-64 pb-20 lg:pb-0">
              {renderCurrentScreen()}
            </div>
          </div>
        </ChatProvider>
      </GanttChartsProvider>
    </ConnectionsProvider>
  );
};

function App() {
  return (
    <ThemeProvider>
      <AppProvider>
        <AppContent />
      </AppProvider>
    </ThemeProvider>
  );
}

export default App;